import type { FC } from 'react'
import ListHeader, { ListHeaderProps } from '@/components/molecules/listHeader'
import { Box } from '@/components/atoms/Grid'
import type { BridgeProduct } from 'types/bridgeProduct'
import { Translate } from 'next-translate'
import useTranslation from 'next-translate/useTranslation'
import { withProductDetailPopup } from '@/HOC/withProductDetailPopup'
import type { ShowProductModal } from '@/HOC/withProductDetailPopup'
import type { ListingData } from 'types/listingData'
import { CategoryType } from 'types/categoryType'
import type { ProductListingFetcher } from '@/components/organisms/productListing/productListingFetcher'
import BannersLayout, { BannersType, PreloadMode } from './bannersLayout'
import { RecommendedIconDefault } from '../atoms/icons/RecommendedIconDefault'
import CategoryProductBanner from '@/components/organisms/categoryProductBanner'
import { Category, HomeBanner } from '@/network/graphql.g'
import { SlugPathItem } from '@/types/slugPath'
import ProductListingTitleCountInfoRedesign from '@/components/molecules/productListingTitleCountInfoRedesign'
import ProductListingPathHeaderRedesign from '@/components/molecules/productListingPathHeaderRedesign'
import TopCategoriesSliderRedesign from '@/components/molecules/topCategoriesSliderRedesign'
import { Desktop, MediaContextProvider, Mobile } from '@/components/atoms/media'
import ProductsContainerRedesign from '@/components/organisms/productsContainerRedesign'

type CategoryProductsContainerRedesignProps = {
  data: ListingData<BridgeProduct>
  showProductModal?: ShowProductModal
  showPager?: boolean
  productListingFetcher: ProductListingFetcher
  list: string
  banners: HomeBanner[]
  isMain: Category
  title: string
  paths: SlugPathItem[]
  count: number
  carouselTopCategories: Category[]
  description?: string
} & Rest

const CategoryProductsContainerRedesign: FC<CategoryProductsContainerRedesignProps> =
  ({
    data,
    showProductModal,
    showPager = false,
    productListingFetcher,
    list,
    banners,
    isMain,
    title,
    paths,
    count,
    carouselTopCategories,
    description,
    ...rest
  }) => {
    const { t } = useTranslation('common')
    return (
      <Box
        textAlign="center"
        mt={{ desktop: !isMain ? 48 : 0 }}
        data-testid="productListingContainer"
        {...rest}
      >
        {!isMain && (
          <CategoryProductBanner
            banners={banners}
            preload
            preloadMode={PreloadMode.First}
          />
        )}
        <MediaContextProvider>
          <Desktop>
            <ProductListingPathHeaderRedesign title={title} paths={paths} />
          </Desktop>
        </MediaContextProvider>
        <ProductListingTitleCountInfoRedesign
          title={title}
          additionalInfo={t('Common.Products', {
            count
          })}
        />
        <MediaContextProvider>
          <Mobile>
            <TopCategoriesSliderRedesign
              carouselTopCategories={carouselTopCategories}
            />
          </Mobile>
        </MediaContextProvider>
        {data?.banners?.length ? (
          <BannersLayout
            banners={data.banners}
            gridColumnGap={15}
            mb={{ desktop: 50, tablet: 40, mobile: 20 }}
            bannerDst={BannersType.MainCategory}
            preload
            preloadMode={PreloadMode.First}
          />
        ) : null}
        {data.type !== undefined && (
          <ListHeader {...makeCategoryListHeader(data.type, t)} />
        )}
        <ProductsContainerRedesign
          fetcher={productListingFetcher}
          data={data}
          action={showProductModal}
          showPager={showPager}
          list={list}
          description={description}
        />
      </Box>
    )
  }

export default withProductDetailPopup(CategoryProductsContainerRedesign)

const makeCategoryListHeader = (
  type: CategoryType,
  t: Translate
): ListHeaderProps => {
  switch (type) {
    case CategoryType.maybeInterestedIn:
      return {
        title: t('Category.YouMayLike.title'),
        description: {
          IconElement: RecommendedIconDefault,
          text: t('Category.YouMayLike.description')
        }
      }
  }
}
